import {
  getPathFromState,
  LinkingOptions,
  NavigationContainer,
  useNavigationContainerRef,
} from "@react-navigation/native";
import * as Linking from "expo-linking";
import * as Notifications from "expo-notifications";
import { useSetAtom } from "jotai";
import React, { useCallback, useEffect, useMemo } from "react";

import { Loader } from "@whoosh/design-system/src/components/display";

import { preAuthenticatedPath_atom } from "../../features/Authentication/atoms";
import { getRouteAfterLoginFromUrl } from "../../features/Authentication/utils/resolvePostLoginRedirect";
import { navigationRef_atom } from "../../features/Navigation";
import { config } from "./../../navigators/routeConfig";
import { SessionChecker } from "./SessionChecker";
import { preserveGoogleLinker } from "./preserveGoogleLinker";

const prefix = [
  Linking.createURL("/"),
  "https://app.whoosh.io",
  "https://demo.whoosh.io",
];
export const NavigatorRenderer = () => {
  const navigationRef = useNavigationContainerRef();
  const setNavigationRefAtom = useSetAtom(navigationRef_atom);
  const setPreAuthenticatedPath = useSetAtom(preAuthenticatedPath_atom);

  const captureRouteAfterLogin = useCallback(
    (url?: null | string) => {
      const routeAfterLogin = getRouteAfterLoginFromUrl(url);

      if (routeAfterLogin) {
        setPreAuthenticatedPath(routeAfterLogin);
      }
    },
    [setPreAuthenticatedPath]
  );

  const linking = useMemo<LinkingOptions<ReactNavigation.RootParamList>>(
    () => ({
      prefixes: prefix,
      config,
      getPathFromState: (state, options) =>
        preserveGoogleLinker(
          getPathFromState(state, options),
          typeof window === "undefined" ? undefined : window.location?.href
        ),
      getInitialURL: async () => {
        const initialUrl = await Linking.getInitialURL();

        if (initialUrl != null) {
          captureRouteAfterLogin(initialUrl);
          return initialUrl;
        }

        const response = await Notifications.getLastNotificationResponseAsync();
        const notificationUrl = (
          response?.notification?.request?.content?.data as any
        )?.booking_cta?.url;
        captureRouteAfterLogin(notificationUrl);
        return notificationUrl;
      },
      subscribe(listener) {
        const handleUrl = (url?: null | string) => {
          if (!url) {
            return;
          }

          captureRouteAfterLogin(url);
          listener(url);
        };
        const onReceiveURL = ({ url }: { url: string }) => handleUrl(url);

        // Listen to incoming links from deep linking
        const eventListenerSubscription = Linking.addEventListener(
          "url",
          onReceiveURL
        );

        // Listen to Expo push notifications
        const subscription =
          Notifications.addNotificationResponseReceivedListener((response) => {
            const url = (response?.notification?.request?.content?.data as any)
              ?.booking_cta?.url;
            handleUrl(url);
          });

        return () => {
          // Clean up the event listeners
          eventListenerSubscription.remove();
          subscription.remove();
        };
      },
    }),
    [captureRouteAfterLogin]
  );

  useEffect(() => {
    setNavigationRefAtom(navigationRef);
  }, [navigationRef, setNavigationRefAtom]);

  return (
    <NavigationContainer
      linking={linking}
      ref={navigationRef}
      fallback={<Loader />}
    >
      <SessionChecker />
    </NavigationContainer>
  );
};
